import {useState, useEffect, useCallback, useRef} from 'react'
import {useRouteMatch} from 'react-router'
import {useOrigin} from '@salesforce/pwa-kit-react-sdk/ssr/universal/hooks'
import {MRT_API_BASEPATH} from '../ssr/constants'
import {useCurrentCustomer} from '@salesforce/retail-react-app/app/hooks/use-current-customer'
import {useAccessToken} from '@salesforce/commerce-sdk-react'
import {useLocaleCountry} from '../hooks/use-locale-custom'

const appendIfDefined = (queryParams, key, value) => {
    if (value !== undefined && value !== null && value !== '') {
        queryParams.set(key, String(value))
    }
}

const useOrderDetails = (guest = false) => {
    const appOrigin = useOrigin(false)
    const {data: customer, isFetched: isCustomerFetched} = useCurrentCustomer()
    const {getTokenWhenReady} = useAccessToken()
    const [isLoading, setIsLoading] = useState(false)
    const [order, setOrder] = useState(true)
    const {params} = useRouteMatch()
    const country = useLocaleCountry()
    const {orderId, orderNo, originalOrderId} = params
    const getTokenWhenReadyRef = useRef(getTokenWhenReady)

    useEffect(() => {
        getTokenWhenReadyRef.current = getTokenWhenReady
    }, [getTokenWhenReady])

    const fetchOrderDetails = useCallback(async () => {
        try {
            setIsLoading(true)
            const queryParams = new URLSearchParams()
            appendIfDefined(queryParams, 'orderSummaryId', orderId)
            appendIfDefined(queryParams, 'gigyaId', customer?.c_gigyaId)
            appendIfDefined(queryParams, 'orderNumber', orderNo)
            appendIfDefined(queryParams, 'originalOrderId', originalOrderId)
            appendIfDefined(queryParams, 'country', country)
            appendIfDefined(queryParams, 'email', customer?.email)
            appendIfDefined(queryParams, 'guest', guest)

            const headers = {'Content-Type': 'application/json'}
            if (!guest) {
                const shopperToken = await getTokenWhenReadyRef.current()
                if (shopperToken) {
                    headers['x-commercecloud-token'] = `Bearer ${shopperToken}`
                }
                const shopperCustomerId = customer?.customerId || customer?.id
                if (shopperCustomerId) {
                    headers.customerId = shopperCustomerId
                }
            }

            const response = await fetch(
                `${appOrigin}${MRT_API_BASEPATH}/get-order-details-oms?${queryParams.toString()}`,
                {
                    method: 'POST',
                    headers,
                },
            )
            const result = await response.json()
            if (result.success) {
                setOrder({
                    ...result.order,
                })
            }
        } catch (error) {
            // TODO:
            console.error('Error fetching order details:', error)
        } finally {
            setIsLoading(false)
        }
    }, [orderId, orderNo, originalOrderId, customer?.c_gigyaId])

    useEffect(() => {
        if (!guest && !isCustomerFetched) {
            return
        }
        if (!orderId && (!customer?.c_gigyaId || !orderNo)) return
        fetchOrderDetails()
    }, [isCustomerFetched, orderId, customer?.c_gigyaId, orderNo, fetchOrderDetails])

    const refetchOrder = useCallback(() => {
        fetchOrderDetails()
    }, [fetchOrderDetails])

    return {order, isLoading, refetchOrder}
}

/**
 * Hook per ottenere i dettagli di un ordine basandosi su un oggetto `order` passato come argomento.
 * @param {Object} order - Oggetto ordine contenente i parametri necessari.
 * @returns {Object} - Dettagli dell'ordine, stato di caricamento e eventuali errori.
 */
const useOrderDetailsFromOrder = (order, guest = false) => {
    const [orderDetails, setOrderDetails] = useState(null)
    const {data: customer} = useCurrentCustomer()
    const {getTokenWhenReady} = useAccessToken()
    const country = useLocaleCountry()
    const [isLoading, setIsLoading] = useState(true)
    const [error, setError] = useState(null)
    const appOrigin = useOrigin(false)
    const getTokenWhenReadyRef = useRef(getTokenWhenReady)

    useEffect(() => {
        getTokenWhenReadyRef.current = getTokenWhenReady
    }, [getTokenWhenReady])

    useEffect(() => {
        const fetchOrderDetails = async () => {
            if (!order?.Id || !order?.OrderNumber || !order?.OriginalOrderId) {
                setError('Missing required order parameters')
                setIsLoading(false)
                return
            }

            try {
                setIsLoading(true)

                const queryParams = new URLSearchParams()
                appendIfDefined(queryParams, 'orderSummaryId', order.Id)
                appendIfDefined(queryParams, 'orderNumber', order.OrderNumber)
                appendIfDefined(queryParams, 'originalOrderId', order.OriginalOrderId)
                appendIfDefined(queryParams, 'gigyaId', customer?.c_gigyaId)
                appendIfDefined(queryParams, 'country', country)
                appendIfDefined(queryParams, 'email', customer?.email)
                appendIfDefined(queryParams, 'guest', guest)

                const headers = {'Content-Type': 'application/json'}
                if (!guest) {
                    const shopperToken = await getTokenWhenReadyRef.current()
                    if (shopperToken) {
                        headers['x-commercecloud-token'] = `Bearer ${shopperToken}`
                    }
                    const shopperCustomerId = customer?.customerId || customer?.id
                    if (shopperCustomerId) {
                        headers.customerId = shopperCustomerId
                    }
                }

                const response = await fetch(
                    `${appOrigin}${MRT_API_BASEPATH}/get-order-details-oms?${queryParams.toString()}`,
                    {
                        method: 'POST',
                        headers,
                    },
                )

                const result = await response.json()
                if (result.success) {
                    setOrderDetails(result.order)
                } else {
                    setError('Failed to fetch order details: ' + result.error)
                }
            } catch (err) {
                console.error('Error fetching order details:', err)
                setError('Error fetching order details: ' + err.message)
            } finally {
                setIsLoading(false)
            }
        }

        if (order) {
            fetchOrderDetails()
        }
    }, [order, appOrigin])

    return {orderDetails, isLoading, error}
}

export {useOrderDetails, useOrderDetailsFromOrder}
